import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAssessment, useUploadRecording, useProcessAssessment } from "@/lib/api/assessments";
import { useAssessmentStatus } from "@/lib/api/websocket";
import { useCurrentUser } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StrandBadge from "@/components/StrandBadge";
import ModeBadge from "@/components/ModeBadge";
import { Mic, Square, Play, RotateCcw, Send, Clock, Info, Loader2, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { formatTime } from "@/lib/audio";

type RecordingState = "idle" | "recording" | "recorded" | "uploading" | "processing";

// Input level (0-100 scale from the analyser) below which we treat the mic as
// silent for the live "we're not hearing you" hint. Real speech sits well
// above this; a muted or dead input stays near zero.
const SILENCE_LEVEL = 3;

interface AssessmentData {
  id: string;
  mode: "presenting" | "explaining" | "persuading";
  prompt: string;
  time_limit_seconds: number;
  status: string;
}

function getModeFromString(mode: string): "presentational" | "exploratory" | "collaborative" {
  if (mode === "presenting") return "presentational";
  if (mode === "explaining") return "exploratory";
  return "collaborative";
}


function getSuccessCriteria(mode: string): string[] {
  switch (mode) {
    case "presenting":
      return [
        "Project your voice so everyone can hear clearly",
        "Use descriptive language to paint a picture",
        "Structure your talk with a clear beginning, middle, and end",
        "Vary your pace — slow down for important points",
      ];
    case "explaining":
      return [
        "Break down the steps clearly in order",
        "Use simple words to explain tricky ideas",
        "Check your listener understands with questions",
        "Give examples to make it clearer",
      ];
    case "persuading":
      return [
        "Give strong reasons why your idea is good",
        "Think about what others might think and answer them",
        "Use exciting words to make people care",
        "End with a clear action you want people to take",
      ];
    default:
      return [
        "Speak clearly so everyone can hear",
        "Stay on topic",
        "Share your ideas confidently",
      ];
  }
}

// Safari can't record webm/opus; picking the first format the browser actually
// supports keeps capture working there instead of failing the constructor or
// handing back an empty blob. Falls back to the browser default if none match.
function pickSupportedRecordingMime(): string {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/mpeg",
  ];
  if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported) {
    for (const type of candidates) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
  }
  return "";
}

function mimeToExtension(mime: string): string {
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("mpeg")) return "mp3";
  return "webm";
}

export default function TaskRecordingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: user } = useCurrentUser();
  
  const { data: assessment, isLoading: loadingAssessment } = useAssessment(id || "");
  const uploadRecording = useUploadRecording();
  const processAssessment = useProcessAssessment();

  useAssessmentStatus(id || null);
  
  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [seconds, setSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [micSilent, setMicSilent] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const volumeRafRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingMimeRef = useRef<string>("audio/webm");
  const startingRef = useRef(false);
  // Loudest input level seen this take, plus whether we heard anything in the
  // current second. Together they drive the live "we're not hearing your mic"
  // hint so a dead capture shows up while recording, not after 3 minutes.
  const peakLevelRef = useRef(0);
  const heardSoundRef = useRef(false);
  const silentSecondsRef = useRef(0);
  
  const maxTime = assessment?.time_limit_seconds || 180;
  
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (volumeRafRef.current) {
        cancelAnimationFrame(volumeRafRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);
  
  const startRecording = useCallback(async () => {
    // Guard against a re-entrant start. The record button stays mounted and
    // clickable during the async getUserMedia call (recordingState only flips
    // afterwards), so a double-tap would otherwise fire this twice and stack a
    // second interval, making the timer count up faster than real time. The lock
    // is a synchronous ref so the second call bails before any await.
    if (startingRef.current || mediaRecorderRef.current?.state === "recording") {
      return;
    }
    startingRef.current = true;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (volumeRafRef.current) {
      cancelAnimationFrame(volumeRafRef.current);
      volumeRafRef.current = null;
    }
    try {
      setError("");
      setMicSilent(false);
      peakLevelRef.current = 0;
      heardSoundRef.current = false;
      silentSecondsRef.current = 0;
      // Capture the raw mic. This page used to set echoCancellation +
      // noiseSuppression, but those gate quiet or ambient audio (e.g. a clip
      // played near the mic) down to near-silence, which produced empty
      // recordings and blank transcripts even though the timer ran the full
      // length. The demo recorder uses plain `audio: true` and captures fine,
      // and the pipeline scores raw acoustic features anyway, so the
      // unprocessed signal is the one we actually want.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      
      // Wire up volume analyser
      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const tickVolume = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const level = Math.min(100, (avg / 128) * 100);
        setVolumeLevel(level);
        if (level > SILENCE_LEVEL) {
          heardSoundRef.current = true;
          if (level > peakLevelRef.current) peakLevelRef.current = level;
        }
        volumeRafRef.current = requestAnimationFrame(tickVolume);
      };
      tickVolume();

      const mimeType = pickSupportedRecordingMime();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      // Use what the recorder actually reports, so the upload's type/extension
      // matches the bytes even when we fell back to the browser default.
      recordingMimeRef.current = recorder.mimeType || mimeType || "audio/webm";

      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onerror = (e) => {
        // A mid-recording recorder failure otherwise goes unhandled: the timer
        // keeps counting while no more audio is captured, leaving a take that
        // looks complete but is empty. Surface it and stop cleanly instead.
        console.error("MediaRecorder error:", e);
        setError("Recording stopped unexpectedly. Please check your microphone and record again.");
        stopRecording();
      };

      recorder.onstop = () => {
        const blobType = recorder.mimeType || recordingMimeRef.current;
        // Keep the ref in sync with what was actually recorded so the upload's
        // type and extension match the bytes (matters when we fell back to the
        // browser default and only learn the real format here).
        recordingMimeRef.current = blobType;
        const blob = new Blob(chunks, { type: blobType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
      };
      
      mediaRecorderRef.current = recorder;
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      recorder.start(100);
      
      setRecordingState("recording");
      setSeconds(0);
      
      timerRef.current = setInterval(() => {
        // Watch for a dead mic: if a whole second goes by with no input, count
        // it, and after a few in a row warn live so the student can fix it
        // before wasting the full take.
        if (heardSoundRef.current) {
          silentSecondsRef.current = 0;
          setMicSilent(false);
        } else {
          silentSecondsRef.current += 1;
          if (silentSecondsRef.current >= 3) setMicSilent(true);
        }
        heardSoundRef.current = false;

        setSeconds((prev) => {
          if (prev >= maxTime - 1) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (err) {
      console.error("Failed to start recording:", err);
      setError("Could not access microphone. Please check permissions.");
      toast({
        title: "Recording Error",
        description: "Could not access microphone. Please allow microphone access.",
        variant: "destructive",
      });
    } finally {
      startingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxTime, toast]);
  
  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      // Flush any buffered audio before stopping. Some browsers don't deliver
      // a final dataavailable on stop, which would otherwise leave the blob
      // empty even though audio was captured.
      recorder.requestData();
      recorder.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (volumeRafRef.current) {
      cancelAnimationFrame(volumeRafRef.current);
      volumeRafRef.current = null;
    }
    setVolumeLevel(0);
    setRecordingState("recorded");
  }, []);
  
  const handleReRecord = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl("");
    setAudioChunks([]);
    setSeconds(0);
    setRecordingState("idle");
  };
  
  const handleSubmit = async () => {
    if (!audioBlob || !id) {
      toast({
        title: "Error",
        description: "No recording to submit",
        variant: "destructive",
      });
      return;
    }

    // A real opus capture is several KB even for a second or two; anything this
    // small means the mic produced nothing (empty/broken capture). Uploading it
    // sends silence to the pipeline, which fails processing and lands the student
    // on the "contact your teacher" error. Catch it here and let them re-record.
    if (audioBlob.size < 2048) {
      setRecordingState("recorded");
      setError("That recording came through empty. Check your microphone and record again.");
      toast({
        title: "Empty recording",
        description: "We couldn't capture any audio. Check your microphone and try again.",
        variant: "destructive",
      });
      return;
    }

    setRecordingState("uploading");
    
    try {
      await uploadRecording.mutateAsync({
        assessmentId: id,
        file: new File(
          [audioBlob],
          `recording.${mimeToExtension(recordingMimeRef.current)}`,
          { type: recordingMimeRef.current }
        ),
      });
      
      setRecordingState("processing");
      
      await processAssessment.mutateAsync(id);
      
      toast({
        title: "Recording Submitted",
        description: "Your recording has been submitted for feedback!",
      });
      
      navigate(`/student/feedback/${id}`);

    } catch (err) {
      console.error("Submit failed:", err);
      setError("Failed to submit recording. Please try again.");
      setRecordingState("recorded");
      toast({
        title: "Upload Failed",
        description: "Could not submit your recording. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  if (loadingAssessment) {
    return (
      <AppLayout role="student">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }
  
  if (!assessment) {
    return (
      <AppLayout role="student">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Assessment Not Found</h2>
              <p className="text-muted-foreground mb-4">
                This assessment doesn't exist or has been removed.
              </p>
              <Button onClick={() => navigate("/student")}>
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }
  
  // Only a fresh (pending) assessment can be recorded. Once a recording is in
  // flight (uploading/processing) or further along (draft_ready, under_review,
  // signed_off, error) the backend rejects a new recording with a 400, so don't
  // even let the recorder open — send them to the feedback page which shows the
  // right state. The one exception is the active submitter on this page: once
  // they hit submit the server status flips to uploading/processing, but we keep
  // them here to show the submitting/processing UI until handleSubmit navigates.
  const isSubmitting = recordingState === "uploading" || recordingState === "processing";
  if (assessment.status !== "pending" && !isSubmitting) {
    return <Navigate to={`/student/feedback/${id}`} replace />;
  }

  const mode = getModeFromString(assessment.mode);
  const successCriteria = getSuccessCriteria(assessment.mode);

  return (
    <AppLayout role="student">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Task info */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <ModeBadge mode={mode} />
            <StrandBadge strand="physical" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {assessment.prompt_name || "Your Speaking Task"}
          </h1>
          <p className="text-muted-foreground text-sm">
            Speak naturally. There are no right or wrong answers.
          </p>
        </motion.div>
        
        {/* Success criteria */}
        {/* <Card className="bg-secondary/50 border-none">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-primary" />
              <p className="text-sm font-medium text-foreground">Success Criteria</p>
            </div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {successCriteria.map((criteria, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                  {criteria}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card> */}
        {/* Success criteria */}
        <Card className="bg-secondary/50 border-none">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-primary" />
              <p className="text-sm font-medium text-foreground">Instructions</p>
            </div>
            <div className="space-y-2 text-md text-pretty text-muted-foreground">
              {assessment.prompt?.instructions || "Speak naturally about the task for the time given."}
            </div>
          </CardContent>
        </Card>
        
        {/* Error message */}
        {error && (
          <Card className="border-destructive bg-destructive/10">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}
        
        {/* Recording area */}
        <Card>
          <CardContent className="p-8 flex flex-col items-center">
            <AnimatePresence mode="wait">
              {recordingState === "idle" && (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center gap-4"
                >
                  <div className="w-24 h-24 rounded-full bg-secondary flex items-center justify-center">
                    <Mic className="w-10 h-10 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Ready to record your {assessment.mode}?
                  </p>
                  <Button size="lg" onClick={startRecording} className="gap-2">
                    <Play className="w-4 h-4" /> Start Recording
                  </Button>
                </motion.div>
              )}
              
              {recordingState === "recording" && (
                <motion.div
                  key="recording"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center gap-4"
                >
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-destructive/10 flex items-center justify-center animate-pulse">
                      <Mic className="w-10 h-10 text-destructive" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive animate-pulse" />
                  </div>
                  {/* Volume bar */}
                  <div className="w-48 h-3 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-75"
                      style={{
                        width: `${volumeLevel}%`,
                        backgroundColor: volumeLevel > 0 ? "#6B9EC8" : undefined,
                      }}
                    />
                  </div>

                  {micSilent && (
                    <div className="flex items-center gap-2 text-warning text-sm text-center">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>We're not picking up any sound. Check your mic isn't muted and speak up.</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-foreground">
                    <Clock className="w-4 h-4" />
                    <span className="text-2xl font-mono font-semibold">
                      {formatTime(seconds)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      / {formatTime(maxTime)}
                    </span>
                  </div>
                  <Button
                    size="lg"
                    variant="destructive"
                    onClick={stopRecording}
                    className="gap-2"
                  >
                    <Square className="w-4 h-4" /> Stop Recording
                  </Button>
                </motion.div>
              )}
              
              {recordingState === "recorded" && (
                <motion.div
                  key="recorded"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center gap-4 w-full"
                >
                  <div className="w-24 h-24 rounded-full bg-success/10 flex items-center justify-center">
                    <Mic className="w-10 h-10 text-success" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Recording complete — {formatTime(seconds)}
                  </p>
                  
                  {/* Audio preview */}
                  {audioUrl && (
                    <audio 
                      src={audioUrl} 
                      controls 
                      className="w-full h-12"
                    />
                  )}
                  
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={handleReRecord}
                      className="gap-2"
                    >
                      <RotateCcw className="w-4 h-4" /> Re-record
                    </Button>
                    <Button 
                      onClick={handleSubmit} 
                      className="gap-2"
                    >
                      <Send className="w-4 h-4" /> Submit for Feedback
                    </Button>
                  </div>
                </motion.div>
              )}
              
              {recordingState === "uploading" && (
                <motion.div
                  key="uploading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center gap-4"
                >
                  <Loader2 className="w-12 h-12 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Uploading your recording...</p>
                </motion.div>
              )}
              
              {recordingState === "processing" && (
                <motion.div
                  key="processing"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center gap-4"
                >
                  <Loader2 className="w-12 h-12 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Processing your recording...</p>
                  <p className="text-xs text-muted-foreground">
                    This may take a minute or two
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}